import React, { useState, useMemo, useRef } from 'react';
import { Card, Row, Col, ListGroup, Form, Button, Alert, Modal, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import JoditEditor from 'jodit-react';
import { safeFormatDate, formatDateToDayId, formatDateWithLocale, sanitizeHTML } from '../shared/utils';
import { getActivityIcon } from '../shared/activityUtils';
import { v4 as uuidv4 } from 'uuid';
import TodoItem from './TodoItem';
import { QRCodeSVG } from 'qrcode.react';
import './InfoTab.css';

function OverviewTab({ parsedData, plan, planId, onSaveChanges, activeTab }) {
  const { t } = useTranslation();
  const [showDateModal, setShowDateModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [startDate, setStartDate] = useState(parsedData.startDate ? parsedData.startDate.split('T')[0] : '');
  const [endDate, setEndDate] = useState(parsedData.endDate ? parsedData.endDate.split('T')[0] : '');
  const [planName, setPlanName] = useState(parsedData.name || '');
  const [planDescription, setPlanDescription] = useState(parsedData.description || '');
  const [planImages, setPlanImages] = useState(parsedData.images || []);
  const [coverImageIndex, setCoverImageIndex] = useState(null);
  const [dateChangeMode, setDateChangeMode] = useState('absolute');
  const [validationError, setValidationError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activitiesToLose, setActivitiesToLose] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');
  const descriptionJoditRef = useRef(null);

  // Jodit config for description (same as Additional Info section)
  const descriptionEditorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    enableDragAndDropFileToEditor: true,
    uploader: { insertImageAsBase64URI: true },
    buttons: ['source', '|', 'bold', 'italic', 'underline', 'strikethrough', '|', 'font', 'fontsize', 'brush', 'paragraph', '|', 'ul', 'ol', '|', 'table', 'link', 'image', '|', 'align', '|', 'undo', 'redo', '|', 'hr', 'eraser', 'copyformat', '|', 'fullsize'],
    buttonsXS: ['bold', 'italic', '|', 'paragraph', '|', 'ul', 'ol', '|', 'link', 'image'],
    cleanHTML: { timeout: 0, removeEmptyElements: false },
    language: 'en',
    toolbarSticky: true,
    toolbarStickyOffset: 0,
    iframe: false,
    disablePlugins: 'about',
    removeButtons: ['about'],
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
    placeholder: t('planDetail.overview.enterPlanDescription'),
    spellcheck: true,
    theme: 'default',
    events: {
      afterInit: (instance) => {
        descriptionJoditRef.current = instance;
      }
    }
  }), [t]);

  // Settings state
  const [collapseCompletedActivities, setCollapseCompletedActivities] = useState(
    parsedData.settings?.collapseCompletedActivities || true
  );
  const [activitiesChatPromptContext, setActivitiesChatPromptContext] = useState(
    parsedData.settings?.activitiesChatPromptContext ?? t('planDetail.days.copyActivitiesToChat.promptOutro')
  );

  const handleEditDates = () => {
    setStartDate(parsedData.startDate ? parsedData.startDate.split('T')[0] : '');
    setEndDate(parsedData.endDate ? parsedData.endDate.split('T')[0] : '');
    setDateChangeMode('absolute');
    setValidationError('');    
    setShowDateModal(true);
  };

  const handleEditDetails = () => {
    setPlanName(parsedData.name || '');
    setPlanDescription(parsedData.description || '');
    const images = parsedData.images?.map(img => ({
      ...img,
      url: typeof img === 'string' ? img : img.url
    })) || [];
    setPlanImages(images);
    
    // Find which image is the current cover image
    let foundIndex = null;
    if (plan?.coverImage) {
      foundIndex = images.findIndex(img => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        return imgUrl === plan.coverImage;
      });
      if (foundIndex === -1) foundIndex = null;
    }
    setCoverImageIndex(foundIndex);
    
    setValidationError('');
    setShowEditDetailsModal(true);
  };

  const handleAddImage = () => {
    setPlanImages([...planImages, {
      url: '',
      sourceUrl: '',
      provider: '',
      attribution: { name: '', url: '' }
    }]);
  };

  const handleRemoveImage = (index) => {
    setPlanImages(planImages.filter((_, i) => i !== index));
  };

  const handleImageUrlChange = (index, newUrl) => {
    setPlanImages(planImages.map((img, i) => {
      if (i === index) {
        return {
          url: newUrl,
          sourceUrl: '',
          provider: '',
          attribution: { name: '', url: '' }
        };
      }
      return img;
    }));
  };

  // Function to check which activities will be lost when dates change
  const checkActivitiesToLose = () => {
    if (!parsedData || !parsedData.days || !startDate || !endDate) {
      return [];
    }

    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);
    newStart.setHours(0, 0, 0, 0);
    newEnd.setHours(23, 59, 59, 999);

    const activitiesToLose = [];

    parsedData.days.forEach((day, dayIndex) => {
      if (!day.activities || day.activities.length === 0) {
        return;
      }

      let willLoseDay = false;

      if (dateChangeMode === 'absolute') {
        // In absolute mode, check if the day's date falls outside the new range
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        willLoseDay = dayDate < newStart || dayDate > newEnd;
      } else {
        // In relative mode, check if the day index is beyond the new day count
        const newDayCount = Math.round((newEnd - newStart) / (1000 * 60 * 60 * 24)) + 1;
        willLoseDay = dayIndex >= newDayCount;
      }

      if (willLoseDay) {
        day.activities.forEach(activity => {
          activitiesToLose.push({
            ...activity,
            dayDate: day.date,
            dayIndex: dayIndex + 1
          });
        });
      }
    });

    return activitiesToLose;
  };

  const validateDates = () => {
    // Validate inputs
    if (!startDate || !endDate) {
      setValidationError(t('planDetail.overview.bothDatesRequired'));
      return false;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setValidationError(t('planDetail.overview.invalidDateFormat'));
      return false;
    }
    
    if (end < start) {
      setValidationError(t('planDetail.overview.endDateAfterStart'));
      return false;
    }

    // Check for activities that will be lost
    const lostActivities = checkActivitiesToLose();
    
    if (lostActivities.length > 0) {
      setActivitiesToLose(lostActivities);
      setShowConfirmModal(true);
      return false; // Don't proceed yet, wait for user confirmation
    }

    // All validation passed
    return true;
  };

  const handleSaveDateChanges = (force = false) => {
    // Clear previous errors
    setValidationError('');
    
    // Skip validation if force=true, otherwise validate
    if (!force && !validateDates()) {
      return;
    }

    // Create a deep copy of parsedData to avoid mutating the original
    const updatedData = JSON.parse(JSON.stringify(parsedData));
    
    // Update plan start and end dates (preserve time parts)
    updatedData.startDate = parsedData.startDate ? 
      `${startDate}T${parsedData.startDate.split('T')[1]}` : 
      `${startDate}T00:00:00.000Z`;
      
    updatedData.endDate = parsedData.endDate ? 
      `${endDate}T${parsedData.endDate.split('T')[1]}` : 
      `${endDate}T23:59:59.999Z`;

    if (dateChangeMode === 'absolute') {
      // Keep activities on the same absolute dates
      // No need to adjust activity dates, but we need to adjust days array
      
      // Get days in the new date range
      const newDaysList = [];
      let currentDate = new Date(startDate);
      const lastDate = new Date(endDate);
      
      while (currentDate <= lastDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayId = formatDateToDayId(currentDate);
        
        // Check if this day already exists in the current plan
        const existingDay = updatedData.days.find(d => 
          new Date(d.date).toISOString().split('T')[0] === dateStr);
        
        if (existingDay) {
          // Update the day ID to match the date format
          existingDay.id = dayId;
          existingDay.date = dateStr;
          newDaysList.push(existingDay);
        } else {
          // Create a new day
          newDaysList.push({
            id: dayId,
            date: dateStr,
            activities: []           
          });
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      updatedData.days = newDaysList;
      
    } else if (dateChangeMode === 'relative') {
      // Keep activities on the same day number (relative position)
      // Create new array of days with adjusted dates
      const newDaysList = [];
      let newStart = new Date(startDate);
      const newEnd = new Date(endDate);
      
      // Determine how many days we need
      const dayCount = Math.round((newEnd - newStart) / (1000 * 60 * 60 * 24)) + 1;
      
      // Create the appropriate number of days
      for (let i = 0; i < dayCount; i++) {
        const currentDate = new Date(newStart);
        currentDate.setDate(newStart.getDate() + i);
        
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayId = formatDateToDayId(currentDate);
        
        // Create or reuse day entry
        const sourceDay = i < updatedData.days.length ? updatedData.days[i] : null;
        
        if (sourceDay) {
          const newDay = {
            ...sourceDay,
            id: dayId,
            date: dateStr,
            activities: sourceDay.activities ? JSON.parse(JSON.stringify(sourceDay.activities)) : []
          };
          
          // Update activities' dates while preserving time
          if (newDay.activities && newDay.activities.length > 0) {
            newDay.activities.forEach(activity => {
              if (activity.startDateTime) {
                const timePart = activity.startDateTime.split('T')[1] || '00:00:00.000Z';
                activity.startDateTime = `${dateStr}T${timePart}`;
              }
              
              if (activity.endDateTime) {
                const timePart = activity.endDateTime.split('T')[1] || '23:59:59.999Z';
                
                // If this is the end of a multi-day activity, we need special handling
                // For simplicity, we're just keeping the end time on the same relative day
                activity.endDateTime = `${dateStr}T${timePart}`;
              }
            });
          }
          
          newDaysList.push(newDay);
        } else {
          // Create a new empty day
          newDaysList.push({
            id: dayId,
            date: dateStr,
            activities: []            
          });
        }
      }
      
      updatedData.days = newDaysList;
    }

    // Save changes
    onSaveChanges(updatedData);
    setShowDateModal(false);
  };
  
  // Function to handle confirmation of date change with lost activities
  const handleConfirmDateChange = () => {
    setShowConfirmModal(false);
    setActivitiesToLose([]);
    handleSaveDateChanges(true);
  };

  // Function to cancel date change
  const handleCancelDateChange = () => {
    setShowConfirmModal(false);
    setActivitiesToLose([]);
  };

  // Function to handle adding a plan-level todo
  const handleAddPlanTodo = (newTodo) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Initialize todos array if it doesn't exist
    updatedData.todos = updatedData.todos || [];
    
    // Add the new todo
    updatedData.todos.push(newTodo);
    
    // Save changes
    onSaveChanges(updatedData);
  };

  // Function to handle updating a todo (either plan or day level)
  const handleChangeTodo = (updatedTodo) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Check if it's a plan-level todo
    if (updatedTodo.source === 'plan') {
      // Initialize todos array if it doesn't exist
      updatedData.todos = updatedData.todos || [];
      
      // Update the todo
      updatedData.todos = updatedData.todos.map(todo =>
        todo.id === updatedTodo.id ? updatedTodo : todo
      );
    } 
    // Otherwise, it's a day-level todo
    else if (updatedTodo.source === 'day' && updatedTodo.dayId) {
      // Find the day and update its todo
      updatedData.days = updatedData.days.map(day => {
        if (day.id === updatedTodo.dayId) {
          // Initialize todos array if it doesn't exist
          day.todos = day.todos || [];
          
          // Update the todo
          day.todos = day.todos.map(todo =>
            todo.id === updatedTodo.id ? updatedTodo : todo
          );
        }
        return day;
      });
    }
    
    // Save changes
    onSaveChanges(updatedData);
  };

  // Function to handle editing a todo (either plan or day level)
  const handleEditTodo = (updatedTodo) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Check if it's a plan-level todo
    if (updatedTodo.source === 'plan') {
      // Initialize todos array if it doesn't exist
      updatedData.todos = updatedData.todos || [];
      
      // Update the todo
      updatedData.todos = updatedData.todos.map(todo =>
        todo.id === updatedTodo.id ? updatedTodo : todo
      );
    } 
    // Otherwise, it's a day-level todo
    else if (updatedTodo.source === 'day' && updatedTodo.dayId) {
      // Find the day and update its todo
      updatedData.days = updatedData.days.map(day => {
        if (day.id === updatedTodo.dayId) {
          // Initialize todos array if it doesn't exist
          day.todos = day.todos || [];
          
          // Update the todo
          day.todos = day.todos.map(todo =>
            todo.id === updatedTodo.id ? updatedTodo : todo
          );
        }
        return day;
      });
    }
    
    // Save changes
    onSaveChanges(updatedData);
  };

  // Function to handle deleting a todo (either plan or day level)
  const handleDeleteTodo = (todoId, source, dayId) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Check if it's a plan-level todo
    if (source === 'plan') {
      // Initialize todos array if it doesn't exist
      updatedData.todos = updatedData.todos || [];
      
      // Filter out the deleted todo
      updatedData.todos = updatedData.todos.filter(todo => todo.id !== todoId);
    } 
    // Otherwise, it's a day-level todo
    else if (source === 'day' && dayId) {
      // Find the day and remove its todo
      updatedData.days = updatedData.days.map(day => {
        if (day.id === dayId) {
          // Initialize todos array if it doesn't exist
          day.todos = day.todos || [];
          
          // Filter out the deleted todo
          day.todos = day.todos.filter(todo => todo.id !== todoId);
        }
        return day;
      });
    }
    
    // Save changes
    onSaveChanges(updatedData);
  };

  // Prepare combined todos list for display
  const allTodos = useMemo(() => {
    const planTodos = parsedData.todos || [];
    
    // Get all day todos and add dayId to each
    const allDayTodos = [];
    if (parsedData.days) {
      parsedData.days.forEach(day => {
        if (day.todos && Array.isArray(day.todos)) {
          // Add dayId and dayNumber to each todo for display
          const dayTodos = day.todos.map(todo => ({
            ...todo,
            dayId: day.id,
            dayNumber: parsedData.days.findIndex(d => d.id === day.id) + 1,
            dayDate: day.date
          }));
          allDayTodos.push(...dayTodos);
        }
      });
    }
    
    // Combine both types of todos
    return [
      ...planTodos.map(todo => ({ ...todo, source: 'plan' })),
      ...allDayTodos.map(todo => ({ ...todo, source: 'day' }))
    ];
  }, [parsedData]);

  // Add conditional rendering for Todo section based on activeTab
  const shouldShowTodos = activeTab === 'overview'; // Only show in overview tab

  // Calculate days until start and end dates
  const calculateDaysUntil = (dateString) => {
    if (!dateString) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part for accurate day calculation
    
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  const daysUntilStart = calculateDaysUntil(parsedData.startDate);
  const daysUntilEnd = calculateDaysUntil(parsedData.endDate);

  const handleSaveDetailsChanges = () => {
    // Validate plan name
    if (!planName.trim()) {
      setValidationError(t('planDetail.overview.planName') + ' ' + t('common.required'));
      return;
    }

    // Create a deep copy of parsedData to avoid mutating the original
    const updatedData = JSON.parse(JSON.stringify(parsedData));

    // Get description from rich editor if available
    const descriptionValue = descriptionJoditRef.current ? descriptionJoditRef.current.value : planDescription;

    // Update plan name and description
    updatedData.name = planName;
    updatedData.description = descriptionValue;
    updatedData.images = planImages;

    // Determine cover image URL
    let coverImageUrl = '';
    if (coverImageIndex !== null && coverImageIndex >= 0 && coverImageIndex < planImages.length) {
      const selectedImage = planImages[coverImageIndex];
      coverImageUrl = typeof selectedImage === 'string' ? selectedImage : (selectedImage.url || '');
    }

    // Save changes to the plan data
    onSaveChanges(updatedData, {
      updatePlanName: true,
      planId: plan.id,
      newName: planName,
      coverImage: coverImageUrl
    });
    
    setShowEditDetailsModal(false);
  };

  // Handle settings change
  const handleSettingChange = (setting, value) => {
    // Update local state
    if (setting === 'collapseCompletedActivities') {
      setCollapseCompletedActivities(value);
    }
    if (setting === 'activitiesChatPromptContext') {
      setActivitiesChatPromptContext(value);
    }
    
    // Update the data and save changes
    const updatedData = { ...parsedData };
    if (!updatedData.settings) {
      updatedData.settings = {};
    }
    updatedData.settings[setting] = value;
    
    onSaveChanges(updatedData);
  };

  return (
    <>
      <Card>
        <Card.Body>
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-3">{parsedData.name || t('planDetail.overview.tripPlan')}</h4>
                <Button 
                  id="edit-details-btn"
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleEditDetails}
                >
                  <i className="bi bi-pencil me-1"></i>
                  {t('planDetail.overview.editDetails')}
                </Button>
              </div>
              {parsedData.description ? (
                <div className="info-content overview-description" dangerouslySetInnerHTML={{ __html: sanitizeHTML(parsedData.description) }} />
              ) : (
                <p className="text-muted mb-0">{t('planDetail.overview.noDescription')}</p>
              )}
              
              {/* Display destination images if available */}
              {parsedData.images && Array.isArray(parsedData.images) && parsedData.images.length > 0 && (
                <div className="destination-images mt-3 mb-4">
                  <Row>
                    {parsedData.images.map((image, index) => {
                      // Skip rendering if image is null, undefined, or not a valid format
                      if (!image || (typeof image !== 'string' && !image.url)) {
                        return null;
                      }
                      
                      // Get the attribution text
                      const attributionText = typeof image.attribution === 'string' 
                        ? image.attribution
                        : (image.attribution?.name || '');
                      
                      // Truncate the attribution text
                      const truncatedAttribution = attributionText.length > 20 
                        ? attributionText.substring(0, 20) + '...'
                        : attributionText;
                      
                      // Only show attribution if it has a value
                      const hasAttribution = truncatedAttribution && truncatedAttribution.trim().length > 0;
                      
                      return (
                        <Col key={index} xs={6} md={4} className="mb-3">
                          <Card className="h-100">
                            <Card.Img 
                              variant="top" 
                              src={typeof image === 'string' ? image : image.url} 
                              alt={typeof image === 'string' ? `Destination image ${index + 1}` : (image.alt || `Destination image ${index + 1}`)}
                              style={{ height: "160px", objectFit: "cover" }}
                            />
                            {typeof image !== 'string' && hasAttribution && (
                              <Card.Footer className="p-1 text-center">
                                <small 
                                  className="text-muted" 
                                  style={{ fontSize: '0.7rem' }}
                                  title={attributionText} // Add tooltip with full text
                                >
                                  {t('planDetail.overview.by')} {truncatedAttribution}
                                </small>
                              </Card.Footer>
                            )}
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                  {parsedData.imagesProvider && (
                    <div className="text-end mt-1">
                      <small className="text-muted">{t('planDetail.overview.imagesProvidedBy')} {typeof parsedData.imagesProvider === 'string' 
                        ? parsedData.imagesProvider 
                        : parsedData.imagesProvider.name || t('planDetail.overview.imageProvider')}</small>
                    </div>
                  )}
                </div>
              )}
            </Col>
            <Col md={4}>
              <Card className="bg-light">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">{t('planDetail.overview.tripDetails')}</h5>
                    <Button 
                      id="edit-dates-btn"
                      variant="outline-primary" 
                      size="sm"
                      onClick={handleEditDates}
                    >
                      <i className="bi bi-calendar me-1"></i>
                      {t('planDetail.overview.editDates')}
                    </Button>
                  </div>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="bg-light px-0">
                      <strong>{t('planDetail.overview.created')}</strong> {formatDateWithLocale(plan.createdDate, 'PPP')}
                    </ListGroup.Item>
                    {parsedData.startDate && (
                      <ListGroup.Item className="bg-light px-0">
                        <strong>{t('planDetail.overview.startDate')}</strong> {formatDateWithLocale(parsedData.startDate)}
                        {daysUntilStart !== null && (
                          <div className="mt-1">
                            <Badge bg={daysUntilStart < 0 ? "secondary" : daysUntilStart <= 7 ? "warning" : "info"}>
                              {daysUntilStart < 0 
                                ? t('planDetail.overview.startedDaysAgo', { count: Math.abs(daysUntilStart) })
                                : daysUntilStart === 0 
                                  ? t('planDetail.overview.today')
                                  : t('planDetail.overview.daysToGo', { count: daysUntilStart })}
                            </Badge>
                          </div>
                        )}
                      </ListGroup.Item>
                    )}
                    {parsedData.endDate && (
                      <ListGroup.Item className="bg-light px-0">
                        <strong>{t('planDetail.overview.endDate')}</strong> {formatDateWithLocale(parsedData.endDate, 'PPP')}
                        {daysUntilEnd !== null && (
                          <div className="mt-1">
                            <Badge bg={daysUntilEnd < 0 ? "secondary" : daysUntilEnd <= 7 ? "warning" : "info"}>
                              {daysUntilEnd < 0 
                                ? t('planDetail.overview.endedDaysAgo', { count: Math.abs(daysUntilEnd) })
                                : daysUntilEnd === 0 
                                  ? t('planDetail.overview.endsToday')
                                  : t('planDetail.overview.daysRemaining', { count: daysUntilEnd })}
                            </Badge>
                          </div>
                        )}
                      </ListGroup.Item>
                    )}
                    {parsedData.groupInfo && (
                      <ListGroup.Item className="bg-light px-0">
                        <strong>{t('planDetail.overview.groupSize')}</strong> {parsedData.groupInfo.numberOfPeople || t('planDetail.overview.notSpecified')}
                        {parsedData.groupInfo.ages && ` (${t('planDetail.overview.ages')} ${parsedData.groupInfo.ages})`}
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Card.Body>
              </Card>
              
              {/* QR Code Card */}
              {planId && (
                <Card className="mt-3">
                  <Card.Body className="text-center">
                    <h6 className="mb-3">{t('planDetail.overview.scanToViewPlan')}</h6>
                    <div className="d-flex flex-column align-items-center">
                      <QRCodeSVG
                        value={`${window.location.origin}/plan-detail/${planId}`}
                        size={200}
                        level="H"
                        includeMargin={true}
                        imageSettings={{
                          src: '/logo.svg',
                          height: 40,
                          width: 40,
                          excavate: true,
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
          
          {shouldShowTodos && (
            <Row className="mb-4">
              <Col md={12}>
                {/* All todos combined (both plan and day specific) */}
                <Card id="all-todos-section" className="mb-3">
                  <Card.Header>
                    <h5 className="m-0">
                      <i className="bi bi-list-check me-2"></i>
                      {t('planDetail.overview.allToDoItems')}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {allTodos.length === 0 ? (
                      <div className="text-center py-3 text-muted">
                        <i className="bi bi-check2-all fs-4 mb-2 d-block"></i>
                        <p className="mb-0">{t('planDetail.overview.noToDoItems')}</p>
                      </div>
                    ) : (
                      <div className="todo-list">
                        {/* Show pending todos first */}
                        {allTodos
                          .filter(todo => todo.status === 'pending')
                          .map(todo => (
                            <TodoItem
                              key={todo.id}
                              todo={todo}
                              onChange={handleChangeTodo}
                              onDelete={() => handleDeleteTodo(todo.id, todo.source, todo.dayId)}
                              onEdit={handleEditTodo}
                              showSource={true}
                            />
                          ))
                        }
                        
                        {/* Then show completed todos */}
                        {allTodos
                          .filter(todo => todo.status === 'completed')
                          .map(todo => (
                            <TodoItem
                              key={todo.id}
                              todo={todo}
                              onChange={handleChangeTodo}
                              onDelete={() => handleDeleteTodo(todo.id, todo.source, todo.dayId)}
                              onEdit={handleEditTodo}
                              showSource={true}
                            />
                          ))
                        }
                      </div>
                    )}
                    
                    {/* Add a form to add new todos directly in the all todos section */}
                    <Form className="mt-3">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder={t('planDetail.overview.addNewToDo')}
                        value={newTodoText || ''}
                        onChange={(e) => setNewTodoText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            handleAddPlanTodo({
                              id: uuidv4(),
                              text: newTodoText,
                              status: 'pending',
                              createdAt: new Date().toISOString(),
                              source: 'plan'
                            });
                            setNewTodoText('');
                          }
                        }}
                        style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      />
                      <Button 
                        variant="primary" 
                        className="mt-2"
                        onClick={() => {
                          if (newTodoText?.trim()) {
                            handleAddPlanTodo({
                              id: uuidv4(),
                              text: newTodoText,
                              status: 'pending',
                              createdAt: new Date().toISOString(),
                              source: 'plan'
                            });
                            setNewTodoText('');
                          }
                        }}
                      >
                        <i className="bi bi-plus"></i> {t('planDetail.overview.add')}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          
          {/* Settings Section - Moved to bottom */}
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h5 className="m-0">
                    <i className="bi bi-gear me-2"></i>
                    {t('planDetail.overview.planSettings')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        id="collapse-completed-activities"
                        label={t('planDetail.overview.collapseCompletedActivities')}
                        checked={collapseCompletedActivities}
                        onChange={(e) => handleSettingChange('collapseCompletedActivities', e.target.checked)}
                      />
                      <Form.Text className="text-muted">
                        {t('planDetail.overview.collapseCompletedActivitiesHelp')}
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label htmlFor="activities-chat-prompt-context">
                        {t('planDetail.overview.activitiesChatPromptContext')}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        id="activities-chat-prompt-context"
                        rows={5}
                        value={activitiesChatPromptContext}
                        onChange={(e) => setActivitiesChatPromptContext(e.target.value)}
                        onBlur={() => handleSettingChange('activitiesChatPromptContext', activitiesChatPromptContext)}
                        placeholder={t('planDetail.days.copyActivitiesToChat.promptOutro')}
                      />
                      <Form.Text className="text-muted">
                        {t('planDetail.overview.activitiesChatPromptContextHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Date Change Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('planDetail.overview.changeTripDates')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationError && (
            <Alert 
              variant="danger"
              dismissible
              onClose={() => setValidationError('')}
            >
              {validationError}
            </Alert>
          )}
          

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.overview.startDate')}</Form.Label>
              <Form.Control 
                type="date" 
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  // Clear validation error when user changes date
                  if (validationError) {
                    setValidationError('');
                  }
                }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.overview.endDate')}</Form.Label>
              <Form.Control 
                type="date" 
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  // Clear validation error when user changes date
                  if (validationError) {
                    setValidationError('');
                  }
                }}
                min={startDate || undefined}
              />
              {startDate && endDate && new Date(endDate) < new Date(startDate) && (
                <Form.Text className="text-danger">
                  {t('planDetail.overview.endDateEqualOrAfter')}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.overview.whenChangingDates')}</Form.Label>
              <Form.Check
                type="radio"
                id="absolute-dates"
                name="dateChangeMode"
                label={t('planDetail.overview.keepAbsoluteDates')}
                checked={dateChangeMode === 'absolute'}
                onChange={() => setDateChangeMode('absolute')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="relative-dates"
                name="dateChangeMode"
                label={t('planDetail.overview.keepRelativeDates')}
                checked={dateChangeMode === 'relative'}
                onChange={() => setDateChangeMode('relative')}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDateModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={() => handleSaveDateChanges(false)}
          >
            {t('planDetail.overview.saveChanges')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Date Change with Lost Activities Modal */}
      <Modal show={showConfirmModal} onHide={handleCancelDateChange} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
            {t('planDetail.overview.confirmDateChange')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>{t('planDetail.overview.warning')}</strong> {t('planDetail.overview.dateChangeWarning', { 
              count: activitiesToLose.length,
              activity: activitiesToLose.length === 1 ? t('planDetail.overview.activity') : t('planDetail.overview.activities')
            })}
          </Alert>

          <div className="mb-3">
            <h6>{t('planDetail.overview.activitiesThatWillBeLost')}</h6>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {activitiesToLose.map((activity, index) => (
                  <ListGroup.Item key={activity.id || index} className="px-0">
                    <div className="d-flex align-items-start">
                      {getActivityIcon(activity.type) === 'route-icon' ? (
                        <span className="route-icon me-2 mt-1" style={{ marginTop: '0.25rem' }}></span>
                      ) : (
                        <i className={`bi ${getActivityIcon(activity.type)} me-2 mt-1`}></i>
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-bold">{activity.name}</div>
                        <small className="text-muted">
                          {t('planDetail.overview.day')} {activity.dayIndex} • {formatDateWithLocale(activity.dayDate, 'PPP')}
                          {activity.type && (
                            <Badge bg="secondary" className="ms-3" style={{ fontSize: '0.7rem' }}>
                              {activity.type}
                            </Badge>
                          )}
                        </small>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          </div>

          <Alert variant="info" className="mb-0">
            <small>
              <i className="bi bi-info-circle me-1"></i>
              {t('planDetail.overview.dateChangeInfo')}
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDateChange}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleConfirmDateChange}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {t('planDetail.overview.proceedAndRemove')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Plan Details Modal */}
      <Modal show={showEditDetailsModal} onHide={() => setShowEditDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('planDetail.overview.editPlanDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationError && (
            <Alert variant="danger" className="mb-3">
              {validationError}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.overview.planName')}</Form.Label>
              <Form.Control 
                type="text" 
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={t('planDetail.overview.enterPlanName')}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.overview.description')}</Form.Label>
              <div className="editor-container">
                <JoditEditor
                  value={planDescription}
                  config={descriptionEditorConfig}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">{t('planDetail.overview.images')}</Form.Label>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleAddImage}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('planDetail.overview.addImage')}
                </Button>
              </div>
              {planImages.map((image, index) => {
                const imageUrl = typeof image === 'string' ? image : image.url;
                return (
                  <div key={index} className="mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="url"
                        placeholder={t('planDetail.overview.enterImageUrl')}
                        value={imageUrl || ''}
                        onChange={(e) => handleImageUrlChange(index, e.target.value)}
                        className="flex-grow-1"
                      />
                      <Form.Check
                        type="radio"
                        id={`coverImage-${index}`}
                        name="coverImage"
                        checked={coverImageIndex === index}
                        onChange={() => setCoverImageIndex(imageUrl ? index : null)}
                        disabled={!imageUrl}
                        className="ms-2"
                        label={
                          <span>
                            {t('planDetail.overview.setAsCover')}
                            <i 
                              className="bi bi-info-circle ms-1" 
                              style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                              data-tooltip-id="cover-image-tooltip"
                              data-tooltip-html={t('planDetail.overview.coverImageHelp')}
                            ></i>
                            <Tooltip id="cover-image-tooltip" place="right" className="activity-modal-tooltip" />
                          </span>
                        }
                      />
                      <Button 
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          handleRemoveImage(index);
                          if (coverImageIndex === index) {
                            setCoverImageIndex(null);
                          } else if (coverImageIndex > index) {
                            setCoverImageIndex(coverImageIndex - 1);
                          }
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditDetailsModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveDetailsChanges}>
            {t('planDetail.overview.saveChanges')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default OverviewTab; 